/**
 * Client-Side Authentication for Vercel Serverless Parser
 * 
 * Provides secure authentication without exposing secrets on the client
 * Security is handled server-side via Origin header validation
 */

// Configuration - Safe for client-side exposure
const API_KEY = process.env.REACT_APP_PARSER_API_KEY || 'privacv-web-client';

/**
 * Generate client authentication token (safe for client-side)
 * @param {string} origin - Request origin
 * @returns {Promise<string>} - Client authentication token
 */
export async function generateClientToken(origin) {
  const timestamp = Math.floor(Date.now() / 1000);
  const sessionId = generateSessionId();
  
  // Create a client token that doesn't contain secrets
  // The Vercel function will validate this based on Origin header and other factors
  const clientToken = {
    apiKey: API_KEY,
    timestamp,
    origin,
    sessionId,
    userAgent: navigator.userAgent ? btoa(navigator.userAgent).substring(0, 32) : 'unknown',
    // Add client fingerprint without sensitive data
    fingerprint: await generateClientFingerprint()
  };
  
  // Encode as base64 for transport
  return btoa(JSON.stringify(clientToken));
}

/**
 * Generate a session ID for the current browser session
 * @returns {string} - Session ID
 */
function generateSessionId() {
  // Try to get existing session ID from sessionStorage
  let sessionId = sessionStorage.getItem('privacv_session_id');
  
  if (!sessionId) {
    // Generate new session ID
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('privacv_session_id', sessionId);
  }
  
  return sessionId;
}

/**
 * Generate a client fingerprint (non-invasive, privacy-friendly)
 * @returns {Promise<string>} - Client fingerprint
 */
async function generateClientFingerprint() {
  // Safely get screen dimensions
  const getScreenInfo = () => {
    try {
      // Check if we're in a browser environment
      if (typeof window !== 'undefined' && window.screen) {
        return `${window.screen.width}x${window.screen.height}`;
      }
      return 'unknown';
    } catch {
      return 'unknown';
    }
  };

  const components = [
    navigator.language || 'unknown',
    getScreenInfo(),
    new Date().getTimezoneOffset().toString(),
    'privacv-client'
  ];
  
  const fingerprint = components.join('|');
  
  // Hash the fingerprint for consistency
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex.substring(0, 16); // Return first 16 characters
}

/**
 * Validate configuration for secure Vercel deployment
 * @returns {Object} - Configuration status
 */
export function validateConfig() {
  const hasApiKey = API_KEY && API_KEY !== '';
  const requireTokens = process.env.REACT_APP_REQUIRE_PARSER_TOKENS === 'true';
  const parserUrl = process.env.REACT_APP_PARSER_API_URL || 'https://privacv-parser.vercel.app';
  
  return {
    hasApiKey,
    requireTokens,
    apiKey: API_KEY,
    parserUrl,
    isProduction: process.env.NODE_ENV === 'production',
    isVercelDeployment: parserUrl.includes('vercel.app'),
    securityModel: 'origin-based'
  };
}

/**
 * Get allowed origins for the current environment
 * @returns {string[]} - Array of allowed origins
 */
export function getAllowedOrigins() {
  const currentOrigin = window.location.origin;
  
  // Default allowed origins based on environment
  const allowedOrigins = [currentOrigin];
  
  // Add development origins
  if (process.env.NODE_ENV === 'development') {
    allowedOrigins.push(
      'http://localhost:3000',
      'http://localhost:3001',
      'http://127.0.0.1:3000'
    );
  }
  
  // Add custom allowed origins from environment
  const customOrigins = process.env.REACT_APP_ALLOWED_ORIGINS;
  if (customOrigins) {
    allowedOrigins.push(...customOrigins.split(',').map(origin => origin.trim()));
  }
  
  return [...new Set(allowedOrigins)]; // Remove duplicates
} 